// Attention-only span helpers. Spans are always ended; AttentionOnlySpanProcessor
// then drops fast-and-successful ones and forwards errored/slow/incident spans.
//
// Built on @opentelemetry/api so it works equally on Node and Bun.
// Bun cannot run OTel auto-instrumentations (they patch node:http which
// Bun implements natively in Zig), so we create spans explicitly here.

import {
  trace,
  context as otelContext,
  propagation,
  SpanStatusCode,
  SpanKind,
} from "@opentelemetry/api";
import type {
  Attributes,
  Context,
  Span,
  TextMapGetter,
  TextMapSetter,
} from "@opentelemetry/api";
import {
  normalizeHttpRoute,
  recordServerError,
  recordServerResponse,
} from "./metrics";

const tracer = trace.getTracer("@atriam/observability", "1.0.0");

export { SpanKind };

// Spans whose own code already recorded the real failure, so a generic
// "HTTP 5xx" status must not overwrite it.
const explainedSpans = new WeakSet<Span>();

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === "string" ? error : "unknown error";
}

/**
 * Mark the active span failed where the failure is a handled value rather
 * than a throw (a caught handler error rendered as a 500, an effect whose
 * provider rejected the command). The span then exports with the real reason.
 */
export function markActiveSpanError(error: unknown): void {
  const span = trace.getActiveSpan();
  if (!span) {
    return;
  }
  if (error instanceof Error) {
    span.recordException(error);
  }
  span.setStatus({ code: SpanStatusCode.ERROR, message: errorText(error) });
  explainedSpans.add(span);
}

const MAX_STATUS_MESSAGE_CHARS = 300;

/** `HTTP 503: upstream_down: reason` from the gateway's JSON error envelope. */
async function describeErrorResponse(res: Response): Promise<string> {
  const fallback = `HTTP ${res.status} response`;
  if (!res.headers.get("content-type")?.includes("json")) {
    return fallback;
  }
  try {
    const body = (await res.clone().json()) as {
      error?: unknown;
      code?: unknown;
      message?: unknown;
    };
    const envelope =
      body.error && typeof body.error === "object"
        ? (body.error as { code?: unknown; message?: unknown })
        : body;
    const parts = [envelope.code, envelope.message, body.error].filter(
      (part, index, all): part is string =>
        typeof part === "string" &&
        part.length > 0 &&
        all.indexOf(part) === index,
    );
    return parts.length === 0
      ? fallback
      : `HTTP ${res.status}: ${parts.join(": ")}`.slice(
          0,
          MAX_STATUS_MESSAGE_CHARS,
        );
  } catch {
    return fallback;
  }
}

export interface WithSpanOptions {
  /** Span kind. Defaults to INTERNAL. */
  kind?: SpanKind;
  /** Slowness threshold in ms. Span is exported only if duration exceeds this OR the work errored. Default 1000. */
  slowMs?: number;
  /** Static attributes to attach. workspace_id/user_id are pulled from baggage automatically. */
  attributes?: Attributes;
  /** Parent context override used by inbound transport wrappers. */
  parentContext?: Context;
}

const headerGetter: TextMapGetter<Headers> = {
  keys: (carrier) => {
    const keys: string[] = [];
    carrier.forEach((_value, key) => {
      keys.push(key);
    });
    return keys;
  },
  get: (carrier, key) => carrier.get(key) ?? undefined,
};

const headerSetter: TextMapSetter<Headers> = {
  set: (carrier, key, value) => carrier.set(key, value),
};

function telemetryUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return rawUrl.split(/[?#]/, 1)[0] ?? rawUrl;
  }
}

/**
 * Run `fn` inside a span. The span is always ended on exit; AttentionOnly
 * SpanProcessor (registered in initObservability) then decides whether to
 * export it based on duration and status. Errored or slow spans reach the
 * exporter; fast successful ones are dropped at the processor level.
 *
 * Usage:
 *   const result = await withSpan("send-sms", { slowMs: 500 }, async () => {
 *     return await telnyx.send(...);
 *   });
 */
export async function withSpan<T>(
  name: string,
  optsOrFn: WithSpanOptions | (() => Promise<T>),
  maybeFn?: () => Promise<T>,
): Promise<T> {
  const fn = typeof optsOrFn === "function" ? optsOrFn : maybeFn!;
  const opts: WithSpanOptions = typeof optsOrFn === "function" ? {} : optsOrFn;

  const started = performance.now();

  // Build the span eagerly so child spans can attach to it.
  const parentContext = opts.parentContext ?? otelContext.active();
  const span: Span = tracer.startSpan(
    name,
    {
      kind: opts.kind ?? SpanKind.INTERNAL,
      attributes: opts.attributes,
    },
    parentContext,
  );
  if (opts.slowMs !== undefined) {
    span.setAttribute("atriam.slow_ms", opts.slowMs);
  }

  try {
    return await otelContext.with(trace.setSpan(parentContext, span), fn);
  } catch (err) {
    if (err instanceof Error) {
      span.recordException(err);
      span.setStatus({ code: SpanStatusCode.ERROR, message: err.message });
    } else {
      span.setStatus({ code: SpanStatusCode.ERROR, message: String(err) });
    }
    throw err;
  } finally {
    const elapsed = performance.now() - started;
    // Tag duration so the AttentionOnlySpanProcessor (app-side) and the
    // collector tail_sampling can decide whether to keep the span.
    span.setAttribute("duration_ms", Math.round(elapsed));
    span.end();
  }
}

/**
 * Drop-in fetch replacement that creates a span around the call.
 * Span exported only if the request errored or was slow.
 *
 *   const res = await tracedFetch("https://api.telnyx.com/...", { method: "POST", ... });
 */
// Use the global `fetch`'s own input/init types so this stays portable across
// Node, Bun, and DOM tsconfigs (DOM has `RequestInfo`, plain Node lib does not).
type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

export async function tracedFetch(
  input: FetchInput,
  init?: FetchInit,
): Promise<Response> {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;
  const method = (init?.method ?? "GET").toUpperCase();
  const target = (() => {
    try {
      return new URL(url).host;
    } catch {
      return url;
    }
  })();

  return withSpan(
    `fetch ${method} ${target}`,
    {
      kind: SpanKind.CLIENT,
      attributes: {
        "http.method": method,
        "http.url": telemetryUrl(url),
        "net.peer.name": target,
      },
    },
    async () => {
      const headers = new Headers(
        typeof Request !== "undefined" && input instanceof Request
          ? input.headers
          : undefined,
      );
      new Headers(init?.headers).forEach((value, key) => {
        headers.set(key, value);
      });
      propagation.inject(otelContext.active(), headers, headerSetter);
      const res = await fetch(input, { ...init, headers });
      // Mark 5xx responses as errors so the span gets exported — but DON'T
      // throw. Throwing here would convert a well-formed upstream error
      // Response into an exception the caller never asked for. Set the span
      // status to ERROR (AttentionOnlySpanProcessor + tail_sampling key on
      // status, so the span still exports) and return the Response unchanged.
      if (res.status >= 500) {
        const span = trace.getActiveSpan();
        span?.setStatus({
          code: SpanStatusCode.ERROR,
          message: `HTTP ${res.status} from ${target}`,
        });
      }
      return res;
    },
  );
}

/**
 * Always-exported one-shot span for explicit "this is interesting" events.
 * The `incident=true` attribute makes the app processor and collector retain
 * the span without inflating operation error rates.
 *
 *   recordIncident("webhook.dlq.write", { event_id, attempts: 10 });
 */
export function recordIncident(
  name: string,
  attributes: Attributes = {},
): void {
  const span = tracer.startSpan(name, {
    kind: SpanKind.INTERNAL,
    attributes,
  });
  span.setAttribute("incident", true);
  span.end();
}

/**
 * Wrap a Bun.serve fetch handler so each incoming request runs inside a span.
 * The span is only exported on error or slowness (per withSpan rules).
 *
 *   Bun.serve({ port, fetch: tracedHandler(async (req) => { ... }) });
 *
 * Extracts incoming W3C `traceparent` and `baggage` headers so the span links
 * into the caller's trace and receives tenant context.
 */
export function tracedHandler<R extends Response | undefined>(
  handler: (req: Request) => Promise<R> | R,
  opts: { slowMs?: number } = {},
): (req: Request) => Promise<R> {
  return async (req: Request): Promise<R> => {
    const url = new URL(req.url);
    const path = url.pathname;
    const method = req.method;

    // Skip /health to keep noise out (matches our existing instrumentation
    // policy for auto-instrumented HTTP).
    if (path === "/health" || path === "/api/health") {
      return await handler(req);
    }

    const parentContext = propagation.extract(
      otelContext.active(),
      req.headers,
      headerGetter,
    );

    const started = performance.now();
    let status = 500;
    try {
      const response = await withSpan(
        `${method} ${path}`,
        {
          kind: SpanKind.SERVER,
          slowMs: opts.slowMs ?? 1000,
          parentContext,
          attributes: {
            "http.method": method,
            "http.target": path,
            "http.url": telemetryUrl(req.url),
          },
        },
        async () => {
          const res = await handler(req);
          const span = trace.getActiveSpan();
          if (res !== undefined) {
            span?.setAttribute("http.status_code", res.status);
            span?.setAttribute("http.response.status_code", res.status);
          }
          // A well-formed 5xx Response is the gateway's structured error envelope
          // (gateway-runtime `toResponse`). DON'T throw it back into Bun.serve —
          // that escapes into Bun's fetch handler with no `error` callback and
          // yields Bun's default "Something went wrong!" body, discarding the
          // real `{ error: { code, message } }`. Instead mark the span ERROR
          // (so it still exports) with the envelope's reason, and return the
          // Response unchanged.
          if (
            res !== undefined &&
            res.status >= 500 &&
            span &&
            !explainedSpans.has(span)
          ) {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: await describeErrorResponse(res),
            });
          }
          return res;
        },
      );
      status = response?.status ?? 101;
      return response;
    } finally {
      const route = normalizeHttpRoute(path);
      recordServerResponse({
        route,
        method,
        status,
        duration_ms: performance.now() - started,
      });
      if (status >= 500) {
        recordServerError({ route, method, status });
      }
    }
  };
}
